import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, OnChanges, SimpleChanges
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { ProductService, Product, SelectedProduct, normalizeSalePrice } from '../../services/products';
import { Customer } from '../../services/customer';
import { EditItemDialogComponent } from './edit-item-dialog/edit-item-dialog-component';
import { SlideButton } from '../slide-button/slide-button';
import { MessageDialogService } from '../../services/message-dialog';
import { MatIconModule } from '@angular/material/icon';
import { getPrice, PricelistService } from '../../services/price-lists';
import { CustomerFavoritesService } from '../../services/customer-favorites-service';

type RowKey = string; // unique per line

@Component({
  selector: 'app-cart',
  templateUrl: './cart-component.html',
  styleUrls: ['./cart-component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    CurrencyPipe,
    MatDialogModule,
    RouterModule,
    SlideButton,
    MatCheckboxModule
  ]
})
export class CartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectedCustomer: Customer | null = null;
  @Input() selectedProducts: SelectedProduct[] = [];
  @Input() disableEdit = false;
  @Input() disableDelete = false;
  @Input() disableDraft = false;
  @Input() disableOrder = false;
  @Input() editButtonLabel = 'Edit Order';
  @Input() readonly = false;

  /** Show selection checkboxes with a select-all in header */
  @Input() showSelection = false;

  /** Emits true when all items are selected; false otherwise */
  @Output() selectionStateChange = new EventEmitter<boolean>();

  @Output() cartUpdated = new EventEmitter<SelectedProduct[]>();
  @Output() placeOrderRequested = new EventEmitter<void>();
  @Output() editRequested = new EventEmitter<void>();
  @Output() saveAsDraftRequested = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();

  products: Product[] = [];
  private destroy$ = new Subject<void>();

  /** Unique selection keys per row (not productId) */
  private _rowKeyCounter = 0;
  private readonly _keyProp: keyof SelectedProduct = '_rowKey' as any; // internal field
  selectedRowKeys = new Set<RowKey>();

  constructor(
    private productService: ProductService,
    private dialog: MatDialog,
    private pricelistService: PricelistService,
  private favoritesService: CustomerFavoritesService,
    private messageDialogService: MessageDialogService
  ) {}

  ngOnInit(): void {
    this.productService.get()
      .pipe(takeUntil(this.destroy$))
      .subscribe(products => (this.products = products));
    this.ensureRowKeys();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProducts']) {
      this.ensureRowKeys();

      // prune selections that no longer exist
      const currentKeys = new Set(
        this.selectedProducts.map(p => (p as any)[this._keyProp] as RowKey)
      );
      for (const k of Array.from(this.selectedRowKeys)) {
        if (!currentKeys.has(k)) this.selectedRowKeys.delete(k);
      }
      this.selectionStateChange.emit(this.allSelected);
    }
  }

  /** Assign a stable unique key to each line item if missing */
  private ensureRowKeys(): void {
    for (const item of this.selectedProducts) {
      const hasOwn = Object.prototype.hasOwnProperty.call(item, this._keyProp);
      if (!hasOwn || !(item as any)[this._keyProp]) {
        (item as any)[this._keyProp] = `row_${Date.now()}_${this._rowKeyCounter++}`;
      }
    }
  }

  get totalAmount(): number {
    return this.selectedProducts.reduce(
      (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
      0
    );
  }

  getProductName(productId: number): string {
    return this.products.find(p => p.id === productId)?.name ?? 'Unknown';
  }

  /* ===== Actions ===== */
  requestPlaceOrder(): void { this.placeOrderRequested.emit(); }
  requestEdit(): void { this.editRequested.emit(); }
  updateCart(): void { this.cartUpdated.emit(this.selectedProducts); }
  saveAsDraft(): void { this.saveAsDraftRequested.emit(); }
  deleteOrder(): void { this.deleteRequested.emit(); }

  openEditDialog(item: SelectedProduct): void {
    if (this.disableEdit || this.readonly) return;

    const dialogRef = this.dialog.open(EditItemDialogComponent, {
      width: '300px',
      data: {
        productName: this.getProductName(item.productId),
        quantity: item.quantity,
        price: item.price
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      if (result.delete === true) {
        const key = (item as any)[this._keyProp] as RowKey;
        this.selectedProducts = this.selectedProducts.filter(
          p => (p as any)[this._keyProp] !== key
        );
        this.selectedRowKeys.delete(key);
      } else {
        const product = this.products.find(p => p.id === item.productId);
        item.quantity = result.quantity;

        const requestedPrice = Number(result.price ?? 0);
        const safeRequestedPrice = Number.isFinite(requestedPrice) ? requestedPrice : 0;
        const minimumPrice = Number(product?.minimum_price ?? 0);
        const hasMinimumPrice = Number.isFinite(minimumPrice) && minimumPrice > 0;

        // A manual price below the product minimum must fall back to the
        // selected customer's pricelist price, not to the generic minimum.
        if (product && hasMinimumPrice && safeRequestedPrice < minimumPrice) {
          const customerPriceListId = this.selectedCustomer?.priceListId;

          this.pricelistService.get()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: pricelists => {
                const matching = pricelists.find(
                  pricelist => pricelist.id === customerPriceListId
                );
                item.price = getPrice(matching, product);
                this.updateCart();
                this.selectionStateChange.emit(this.allSelected);
              },
              error: () => {
                // Preserve the previous safe fallback if pricelist data cannot be read.
                item.price = normalizeSalePrice(product, safeRequestedPrice);
                this.updateCart();
                this.selectionStateChange.emit(this.allSelected);
              }
            });
          return;
        }

        item.price = normalizeSalePrice(product, safeRequestedPrice);
      }
      this.updateCart();
      this.selectionStateChange.emit(this.allSelected);
    });
  }

  /* ===== Selection (by row key) ===== */
  get allSelected(): boolean {
    if (!this.showSelection) return true;
    const total = this.selectedProducts.length;
    return total > 0 && this.selectedRowKeys.size === total;
  }

  toggleSelectAll(checked: boolean): void {
    if (this.readonly) return;
    this.selectedRowKeys.clear();
    if (checked) {
      for (const item of this.selectedProducts) {
        this.selectedRowKeys.add((item as any)[this._keyProp] as RowKey);
      }
    }
    this.selectionStateChange.emit(this.allSelected);
  }

  isSelected(item: SelectedProduct): boolean {
    return this.selectedRowKeys.has((item as any)[this._keyProp] as RowKey);
  }

  toggleSelection(item: SelectedProduct, checked: boolean): void {
    if (this.readonly) return;
    const key = (item as any)[this._keyProp] as RowKey;
    if (checked) this.selectedRowKeys.add(key);
    else this.selectedRowKeys.delete(key);
    this.selectionStateChange.emit(this.allSelected);
  }

  trackByRowKey = (_: number, item: SelectedProduct) =>
    (item as any)[this._keyProp];

  clearCart(): void {
    if (this.readonly || this.disableEdit) return;
    if (!this.selectedProducts?.length) return;
  
    this.messageDialogService.confirm('Clear all items from the cart?').subscribe(confirmed => {
      if (confirmed) {
  
        this.selectedProducts = [];
        this.selectedRowKeys.clear();
      
        // notify parent & reset selection state
        this.updateCart();
        this.selectionStateChange.emit(false);
      }
    });
  }

  reloadPrices(): void {
     this.messageDialogService.confirm('Reload prices?').subscribe(confirmed => {
      if (confirmed) {
        if (this.readonly || this.disableEdit) return;
        if (!this.selectedProducts?.length) return;
      
        const selectedOnly =
          this.showSelection && this.selectedRowKeys && this.selectedRowKeys.size > 0;
      
        const customer = this.selectedCustomer ?? null;
      
        // 1) Get the active pricelist
        this.pricelistService.get().subscribe(pricelists => {
          const matching = pricelists.find(p => p.id === customer?.priceListId);
      
          const applyReprice = (lastPriceMap?: Map<number, any>) => {
            let changed = 0;
      
            const newLines = this.selectedProducts.map(line => {
              const key = (line as any)[this._keyProp] as string;
              if (selectedOnly && !this.selectedRowKeys.has(key)) {
                return line; // untouched
              }
      
              const product = this.products.find(p => p.id === line.productId);
              if (!product) return line;
      
              const last = lastPriceMap?.get(product.id);
              const priceFromLast = last?.last_price;
              const fallback = getPrice(matching, product);
              const newPrice = normalizeSalePrice(product, priceFromLast ?? fallback);
      
              if (newPrice !== line.price) changed++;
              return { ...line, price: newPrice };
            });
      
            this.selectedProducts = newLines;
            this.updateCart();
            this.selectionStateChange.emit(this.allSelected);
      
            // Optional feedback:
            // this.messageDialogService.alert?.(`${changed} item(s) repriced.`);
          };
      
          // 2) Pull last-price cache (if customer present), then apply
          const customerId = customer?.id;
          if (customerId) {
            this.favoritesService.getCachedMap(customerId).subscribe(applyReprice);
          } else {
            applyReprice(undefined);
          }
        });
      }
    });
  }
  
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
